/**
 * Parties API Service - Enhanced wrapper
 *
 * Wraps the generated parties API with enhanced cache invalidation to ensure
 * that when relationships (employment, personal relationships) are modified,
 * the related party queries are properly invalidated.
 *
 * Pattern follows policeDepartmentsService.ts approach.
 */

import {
  type AllPartiesResponse,
  type AllPartiesWithRelationships,
  type CreateOrganization,
  type CreatePerson,
  type DetailedClaimant,
  enhancedApi as generatedApi,
  type ReadOrganization,
  type ReadOrganizationWithEmployees,
  type ReadPerson,
  type ReadPersonWithRelationships,
  type UpdateOrganization,
  type UpdatePerson,
} from './generated/partiesService';

/**
 * Enhanced version of the generated API with granular cache invalidation.
 *
 * When relationships are created/updated/deleted, we invalidate:
 * 1. The specific party detail queries
 * 2. The aggregate "with-relationships" queries
 * 3. The general party list
 */
export const enhancedApi = generatedApi.enhanceEndpoints({
  endpoints: {
    // Persons - provide specific tags for granular invalidation
    personsRetrieve: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'parties' as const, id: arg.id },
              { type: 'parties' as const, id: `person-${arg.id}` },
              'parties' as const,
            ]
          : ['parties' as const],
    },
    personsWithRelationships: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'parties' as const, id: arg.id },
              { type: 'parties' as const, id: `person-with-rel-${arg.id}` },
              'parties' as const,
            ]
          : ['parties' as const],
    },

    // Organizations - provide specific tags
    organizationsRetrieve: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'parties' as const, id: arg.id },
              { type: 'parties' as const, id: `org-${arg.id}` },
              'parties' as const,
            ]
          : ['parties' as const],
    },
    organizationsWithEmployees: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'parties' as const, id: arg.id },
              { type: 'parties' as const, id: `org-with-emp-${arg.id}` },
              'parties' as const,
            ]
          : ['parties' as const],
    },

    // Aggregate views - provide matter-specific tags
    allPartiesList: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { matterId: string },
      ) => [
        { type: 'parties' as const, id: `matter-${arg.matterId}` },
        'parties' as const,
      ],
    },
    allPartiesWithRelationships: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { matterId: string },
      ) => [
        { type: 'parties' as const, id: `matter-with-rel-${arg.matterId}` },
        'parties' as const,
      ],
    },
    claimantDetail: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { matterId: string },
      ) => [
        { type: 'parties' as const, id: `claimant-${arg.matterId}` },
        'parties' as const,
      ],
    },
  },
});

// Re-export all the hooks from the enhanced API
export const {
  useAllPartiesListQuery,
  useAllPartiesWithRelationshipsQuery,
  useClaimantDetailQuery,
  useOrganizationsCreateMutation,
  useOrganizationsDeleteMutation,
  useOrganizationsListQuery,
  useOrganizationsRetrieveQuery,
  useOrganizationsUpdateMutation,
  useOrganizationsApproveFieldMutation,
  useOrganizationsWithEmployeesQuery,
  usePersonsCreateMutation,
  usePersonsDeleteMutation,
  usePersonsListQuery,
  usePersonsRetrieveQuery,
  usePersonsUpdateMutation,
  usePersonsApproveFieldMutation,
  usePersonsWithRelationshipsQuery,
} = enhancedApi;

// Re-export all types
export type {
  AllPartiesResponse,
  AllPartiesWithRelationships,
  CreateOrganization,
  CreatePerson,
  DetailedClaimant,
  ReadOrganization,
  ReadOrganizationWithEmployees,
  ReadPerson,
  ReadPersonWithRelationships,
  UpdateOrganization,
  UpdatePerson,
};
